import {
  getAuthToken,
  storeTokens,
  getRefreshToken,
  redirectToLogin,
} from '@/lib/auth';
import {
  authentication,
  createDirectus,
  rest,
  RestCommand,
  withToken,
  refresh,
  QuerySort,
} from '@directus/sdk';
import { Customer } from './types/customer';
import { Vehicle } from './types/vehicle';
import { VehicleCharacteristic } from './types/characteristic';
import { VehicleDefect } from './types/defect';
import { VehicleProspect } from './types/vehicle-prospect';
import { VehicleFile } from './types/vehicle-file';
import { Service, ServiceRequest } from './types/service';
import { Event } from './types/event';
import { News } from './types/news';

export const DIRECTUS_URL = import.meta.env.VITE_DIRECTUS_URL;


export interface Schema {
  crm_user_information: Customer[];
  crm_vehicle: Vehicle[];
  crm_vehicle_characteristics: VehicleCharacteristic[];
  crm_vehicle_defects: VehicleDefect[];
  crm_vehicle_prospects: VehicleProspect[];
  vehicle_files: VehicleFile[];
  crm_services: Service[];
  crm_service_requests: ServiceRequest[];
  events: Event[];
  news: News[];
}

export type DirectusSort<Item> = QuerySort<Schema, Item>;

interface DirectusError {
  response: {
    status?: number;
    message?: string;
  };
}

const directusClient = createDirectus<Schema>(DIRECTUS_URL)
  .with(rest())
  .with(authentication('json'));

export { directusClient as client };
export default directusClient;

/** Marge avant l'expiration du jeton d'accès pour le renouveler à l'avance. */
const EXPIRY_MARGIN_MS = 30_000;

/** Jeton d'accès valide : renouvelé à l'avance s'il est sur le point d'expirer. */
export async function getValidToken(): Promise<string> {
  const auth = getAuthToken();
  if (!auth) throw new Error('Token non trouvé');
  if (auth.expiry - EXPIRY_MARGIN_MS > Date.now()) return auth.token;
  return refreshToken();
}

function statusOf(error: unknown): number | undefined {
  return (error as DirectusError | undefined)?.response?.status;
}

export async function request<Output>(
  cmd: RestCommand<Output, Schema>
): Promise<Output> {
  const token = await getValidToken();
  try {
    return await directusClient.request<Output>(withToken(token, cmd));
  } catch (error: unknown) {
    if (statusOf(error) === 401) {
      const newToken = await refreshToken();
      return directusClient.request<Output>(withToken(newToken, cmd));
    }
    throw error;
  }
}

/**
 * fetch vers le serveur avec le jeton de l'utilisateur, renouvelé si besoin
 * (et une nouvelle tentative si le serveur répond 401).
 */
export async function authFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const send = (token: string) =>
    fetch(url, {
      ...init,
      headers: { ...(init.headers ?? {}), Authorization: `Bearer ${token}` },
    });
  const response = await send(await getValidToken());
  if (response.status !== 401) return response;
  return send(await refreshToken());
}

/*
 * Un seul renouvellement à la fois : Directus invalide le jeton de
 * renouvellement dès qu'il est utilisé, donc plusieurs requêtes qui
 * expirent en même temps ne doivent pas le réutiliser chacune de leur côté
 * (la 2e échouait et déconnectait l'utilisateur).
 */
let refreshing: Promise<string> | null = null;

export function refreshToken(): Promise<string> {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function doRefresh(): Promise<string> {
  const used = getRefreshToken();
  try {
    if (!used) throw new Error('Refresh token not found');

    const response = await directusClient.request(refresh('json', used));
    if (!response.access_token || !response.refresh_token) {
      throw new Error('Access token or refresh token not found');
    }

    storeTokens(
      response.access_token,
      response.refresh_token,
      response.expires as number
    );
    return response.access_token;
  } catch (error) {
    // Un autre onglet du CRM vient peut-être de renouveler la session :
    // on reprend ses jetons au lieu de déconnecter.
    const current = getAuthToken();
    if (used && getRefreshToken() !== used && current && current.expiry > Date.now()) {
      return current.token;
    }
    // Erreur réseau ou serveur momentanément indisponible : on ne déconnecte pas.
    const status = statusOf(error);
    if (used && status !== undefined && status >= 500) throw error;
    if (used && status === undefined && !(error instanceof Error && /not found/.test(error.message))) {
      throw error;
    }
    console.error('Erreur lors du rafraîchissement du token:', error);
    redirectToLogin();
    throw error;
  }
}
